import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Divider,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined'
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined'

import { useAppDispatch } from '../../redux/store'
import {
  fetchOrganizationLicense,
  changeOrganizationLicense,
} from '../../redux/controlPlane/controlPlaneActions'
import {
  selectControlPlaneToken,
  selectOrganizationBilling,
  selectBillingLoading,
} from '../../redux/controlPlane/controlPlaneSelectors'
import { LicensePlan } from '../../types/license'
import ConfirmDialog from '../../components/containers/ConfirmDialog'
import {
  COMMERCIAL_PACKS,
  PACK_FEATURES,
  CommercialPackKey,
  featureIncludedInPack,
  featureIsSoon,
  toCommercialPack,
  packToBackendKey,
} from '../../utils/licensePacks'

const PACK_ICONS: Record<CommercialPackKey, React.ReactNode> = {
  standard: <ShieldOutlinedIcon />,
  business: <BusinessCenterOutlinedIcon />,
  premium: <WorkspacePremiumOutlinedIcon />,
}

const AccountBilling: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { id } = useParams<{ id: string }>()

  const token = useSelector(selectControlPlaneToken)
  const license = useSelector(selectOrganizationBilling(id || ''))
  const loading = useSelector(selectBillingLoading)

  const [confirmPlan, setConfirmPlan] = useState<LicensePlan | null>(null)

  useEffect(() => {
    if (!token) {
      navigate('/account/login')
      return
    }
    if (id) dispatch(fetchOrganizationLicense(id))
  }, [token, id, navigate, dispatch])

  const handleConfirmChange = async () => {
    if (!id || !confirmPlan) return
    await dispatch(
      changeOrganizationLicense({ organizationId: id, planKey: confirmPlan })
    )
    setConfirmPlan(null)
  }

  const formatDate = (value: string | undefined) =>
    value
      ? new Date(value).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : '—'

  const currentPack = toCommercialPack(license?.planKey)

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fbfbfb' }}>
      <Box sx={{ bgcolor: '#132743', color: 'white', py: 2, px: 3 }}>
        <Container maxWidth="md">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <IconButton
              onClick={() => navigate('/account/organizations')}
              sx={{ color: 'white' }}
              size="small"
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography fontWeight={700}>
              {license?.organizationName ||
                t('account.billingTitle', 'Billing')}
            </Typography>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 4 }}>
        {loading && !license ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : license ? (
          <Stack spacing={3}>
            <Paper sx={{ p: 3, borderRadius: '12px' }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '10px',
                      bgcolor:
                        COMMERCIAL_PACKS.find((p) => p.key === currentPack)
                          ?.accent.bg,
                      color: COMMERCIAL_PACKS.find((p) => p.key === currentPack)
                        ?.accent.text,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {PACK_ICONS[currentPack]}
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      {t(`license.packs.${currentPack}`)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('account.currentPlan', 'Current plan')}
                    </Typography>
                  </Box>
                </Stack>
                <Chip
                  label={
                    license.active
                      ? t('licensePage.status.active', 'Active')
                      : t('licensePage.status.suspended', 'Suspended')
                  }
                  color={license.active ? 'success' : 'error'}
                />
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {t('licensePage.validity', 'Validity')}:{' '}
                {formatDate(license.validFrom)} — {formatDate(license.validTo)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('licensePage.pricingNote', {
                  defaultValue:
                    'Pricing on request — an offer built for your context and cyber maturity level.',
                })}
              </Typography>
            </Paper>

            <Typography variant="h6" fontWeight={700}>
              {t('licensePage.availablePlans', 'Available plans')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 2.5,
              }}
            >
              {COMMERCIAL_PACKS.map((pack) => {
                const isCurrent = currentPack === pack.key
                const features = PACK_FEATURES.filter((f) =>
                  featureIncludedInPack(f, pack.key)
                ).slice(0, 8)
                return (
                  <Paper
                    key={pack.key}
                    sx={{
                      p: 3,
                      borderRadius: '14px',
                      border: `2px solid ${
                        isCurrent ? pack.accent.text : '#e7ecf3'
                      }`,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ color: pack.accent.text }}>
                      {PACK_ICONS[pack.key]}
                    </Box>
                    <Typography fontWeight={700}>
                      {t(`license.packs.${pack.key}`)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t(`license.packTaglines.${pack.key}`)}
                    </Typography>
                    <Stack spacing={0.5}>
                      {features.map((f) => (
                        <Stack
                          key={f.id}
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                        >
                          {featureIsSoon(f) ? (
                            <ScheduleOutlinedIcon
                              sx={{ fontSize: 16, color: '#c98a16' }}
                            />
                          ) : (
                            <CheckCircleOutlineIcon
                              sx={{ fontSize: 16, color: '#0d9b87' }}
                            />
                          )}
                          <Typography variant="body2">
                            {t(`license.packFeatures.${f.labelKey}`)}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                    {isCurrent ? (
                      <Chip
                        label={t('licensePage.currentPlanChip', 'Current plan')}
                        sx={{
                          bgcolor: pack.accent.bg,
                          color: pack.accent.text,
                          alignSelf: 'flex-start',
                        }}
                      />
                    ) : (
                      <Button
                        variant="contained"
                        sx={{ bgcolor: '#244A9A' }}
                        onClick={() =>
                          setConfirmPlan(packToBackendKey(pack.key))
                        }
                      >
                        {t('licensePage.choosePlan', 'Choose this plan')}
                      </Button>
                    )}
                  </Paper>
                )
              })}
            </Box>
          </Stack>
        ) : (
          <Typography color="text.secondary">
            {t('licensePage.noLicense', 'No license found.')}
          </Typography>
        )}
      </Container>

      <ConfirmDialog
        open={Boolean(confirmPlan)}
        onClose={() => setConfirmPlan(null)}
        onConfirm={handleConfirmChange}
        title={t('licensePage.confirmTitle', 'Confirm plan change')}
        message={t('licensePage.confirmMessage', {
          plan: confirmPlan
            ? t(`license.packs.${toCommercialPack(confirmPlan)}`)
            : '',
          defaultValue: `Switch to the ${confirmPlan} plan?`,
        })}
        confirmText={t('licensePage.confirmButton', 'Confirm')}
        cancelText={t('licensePage.cancelButton', 'Cancel')}
        tone="warn"
      />
    </Box>
  )
}

export default AccountBilling
